import { WEBAPP_PREFIX } from '@lx/shared/storage';
import { parseLocale } from '@lx/shared/utils';
import { LOCALE_STORAGE_KEY } from '../types';
import { QuoteToolInitParametersEnum } from '@lx/quote-tool/lite/shared/types';

/**
 * Saves the locale to sessionStorage for persistence across page reloads.
 * @param locale - The locale code to store
 */
export function saveLocaleToStorage(locale: string): void {
  const storageKey = `${WEBAPP_PREFIX}${LOCALE_STORAGE_KEY}`;
  try {
    sessionStorage.setItem(storageKey, JSON.stringify(locale));
  } catch (error) {
    console.warn('Failed to save locale to sessionStorage', error);
  }
}

/**
 * Resolves the application locale based on priority:
 * 1. URL parameter (highest priority)
 * 2. SessionStorage (persisted from previous page load)
 * 3. Default locale (fallback)
 *
 * @param defaultLocale - The fallback locale from environment configuration
 * @returns The resolved locale code
 */
export function resolveLocale(defaultLocale: string): string {
  const queryParamsMap = new URLSearchParams(document.location.search);
  const localeParam = queryParamsMap.get(QuoteToolInitParametersEnum.Locale);

  const storedLocale = getLocaleFromStorage(WEBAPP_PREFIX);
  return parseLocale(localeParam ?? storedLocale ?? defaultLocale);
}

/**
 * Retrieves the locale from sessionStorage.
 * @param storagePrefix - The prefix used for storage keys
 * @returns The stored locale or null if not found or invalid
 */
function getLocaleFromStorage(storagePrefix: string): string | null {
  const storageKey = `${storagePrefix}${LOCALE_STORAGE_KEY}`;
  try {
    const rawStoredLocale = sessionStorage.getItem(storageKey);
    return rawStoredLocale && typeof rawStoredLocale === 'string' ? JSON.parse(rawStoredLocale) : null;
  } catch (error) {
    console.warn('Failed to retrieve locale from sessionStorage', error);
    return null;
  }
}
